/**
 * Optimizer (/optimizer), Classic look (v1.70): the glass/purple screen from
 * ea44aec (v1.68.9, last pre-Floodlight release) with exactly the behaviour of
 * today's screens/SameGameParlay.jsx (Floodlight). The filter rules come from
 * utils/optimizerFilters.js, shared with the Floodlight screen: a same-game
 * parlay names its game once and stacks up to three legs from that game,
 * player markets need a player, changing the game clears the legs, and each
 * mode keeps its own legs/queries. All form logic is hooks/useOptimizerForm.js,
 * shared with the Floodlight screen; this file only renders.
 */
import React from 'react';
import { View, Text, TouchableOpacity, Platform, TextInput, Image, Modal, ScrollView, ActivityIndicator, useWindowDimensions } from 'react-native';
import Appear from '../../components/motion/Appear';
import PressableScale from '../../components/motion/PressableScale';
import SegmentedControl from '../../components/motion/SegmentedControl';
import EdgeLine from '../../components/motion/EdgeLine';
import KineticHeadline from '../../components/motion/KineticHeadline';
import AmbientGlow from '../../components/motion/AmbientGlow';
import Spotlight from '../../components/motion/Spotlight';
import MagneticButton from '../../components/motion/MagneticButton';
import Grain from '../../components/motion/Grain';
import { IS_WEB } from '../../utils/motion';
import injectCss from '../../utils/injectCss';
import { LinearGradient } from 'expo-linear-gradient';
import { isMobile } from '../../utils/responsive';
import NavigationHeader from '../../components/NavigationHeader';
import { BlurView } from 'expo-blur';
import { ScreenBackground } from '../../components/ThemeGradients';
import AutocompleteDropdown from '../../components/AutocompleteDropdown';
import { useTicker } from '../../context/TickerContext';
import GradientButton from '../../components/GradientButton';
import { Ionicons } from '@expo/vector-icons';
import { sportsData, TIME_FILTERS } from '../../data/sportsData';
import { MAX_SGP_LEGS, playerEmptyText } from '../../utils/optimizerFilters';
import { matchPlayerNames } from '../../utils/playerNameSearch';
import { ALL_SPORTSBOOKS, POPULAR_SPORTSBOOKS, BOOST_OPTIONS } from '../../data/optimizerBooks';
import useOptimizerForm from '../../hooks/useOptimizerForm';
import { styles } from './sameGameParlayStyles';

// Optimizer form feel (web): fields brighten on hover and glow green on focus,
// book chips lift, secondary actions spin their icon. Scoped to this screen's
// form so the shared dropdown keeps its look elsewhere. Hover for fine
// pointers only; movement off under reduced motion.
const FORM_CSS = `
[data-opt-form] input { transition: border-color 220ms cubic-bezier(0.2,0,0,1), box-shadow 280ms cubic-bezier(0.2,0,0,1), background-color 220ms; }
/* Selector and chips are TouchableOpacity (inline transition-duration): ours must win, opacity (the press fade) kept. */
[data-opt-field] { transition-property: border-color, box-shadow, background-color, opacity !important; transition-duration: 220ms, 280ms, 220ms, 150ms !important; }
[data-opt-form] input:focus, [data-opt-field]:focus-within { border-color: rgba(52,211,153,0.85) !important; box-shadow: 0 0 0 4px rgba(52,211,153,0.14), 0 12px 32px -14px rgba(52,211,153,0.45); }
/* A field wrapper owns the ring; its inner input stays plain. */
[data-opt-field] input, [data-opt-field] input:hover, [data-opt-field] input:focus { box-shadow: none !important; border-color: transparent !important; background-color: transparent !important; }
[data-opt-chips] > * { transition-property: transform, border-color, background-color, opacity !important; transition-duration: 420ms, 200ms, 200ms, 150ms !important; transition-timing-function: cubic-bezier(0.34,1.4,0.64,1), ease, ease, ease !important; }
[data-opt-btn], [data-opt-icon] { transition: transform 520ms cubic-bezier(0.34,1.4,0.64,1), background-color 220ms, border-color 220ms, box-shadow 280ms; }
@media (hover: hover) and (pointer: fine) {
  [data-opt-form] input:hover:not(:focus), [data-opt-field]:hover:not(:focus-within) { border-color: rgba(255,255,255,0.55) !important; background-color: #202024 !important; }
  [data-opt-chips] > *:hover { transform: translate3d(0, -2px, 0); border-color: rgba(52,211,153,0.6) !important; }
  [data-opt-btn]:hover [data-opt-icon="plus"] { transform: rotate(90deg) scale(1.1); }
  [data-opt-btn]:hover [data-opt-icon="spin"] { transform: rotate(-200deg); }
  [data-opt-btn]:hover [data-opt-icon="stack"] { transform: translate3d(0, -2px, 0) scale(1.1); }
  [data-opt-btn="add"]:hover { box-shadow: 0 14px 36px -14px rgba(139,92,246,0.8); }
}
@media (prefers-reduced-motion: reduce) { [data-opt-chips] > *, [data-opt-btn], [data-opt-icon] { transition: none; transform: none !important; } }
`;

// Classic tier headings: [tier, title, icon].
const TIERS = [[1, 'Most Popular', 'star-outline'], [2, 'US Legal', 'flag-outline'], [3, 'Offshore / Sharp', 'globe-outline'], [4, 'DFS / Props', 'dice-outline']];

const webData = (dataSet) => (IS_WEB ? { dataSet } : {});

const SameGameParlayScreen = () => {
    if (IS_WEB) injectCss('optimizer-form', FORM_CSS);
    const {
        navigateTo, canAddBackgroundQuery, backgroundQueryMode,
        loading, errorMsg, notice, sgp, betType, switchMode, showComingSoon,
        selectedSport, selectedLeague, selectedTeam, selectedPlayer, selectedMarket, selectedMarketCategory,
        selectedSportsbooks, setSelectedSportsbooks, selectedTimeFilter, setSelectedTimeFilter, selectedFixture,
        lineValue, setLineValue, lineDirection, setLineDirection, boostPercent,
        showSportsbooksDropdown, setShowSportsbooksDropdown,
        loadingCacheData, cacheError, leagueFixtures, loadingFixtures, playerOptions, loadingPlayers,
        leagues, games, teams, markets, categories, lineIssue,
        handleSportSelect, handleLeagueSelect, handleTeamSelect, handleMarketSelect, handlePlayerSelect, handleFixtureSelect, handleCategorySelect,
        queries, legs, addQuery, addLeg, removeQuery, removeLeg, clearAllFilters,
        finalQuery, runSearch, toggleSportsbook, myBookNames,
    } = useOptimizerForm();
    const { totalHeaderHeight } = useTicker();
    const { width: winWidth } = useWindowDimensions();
    const compact = winWidth < 768;

    const chip = (name, keyPrefix = '', myBook = false) => {
        const on = selectedSportsbooks.includes(name);
        return (
            <TouchableOpacity
                key={`${keyPrefix}${name}`}
                style={[styles.sportsbookChip, myBook && styles.sportsbookChipMyBook, on && styles.sportsbookChipSelected]}
                onPress={() => toggleSportsbook(name)}
                accessibilityRole="checkbox"
                aria-checked={!!on}
            >
                <Text style={[styles.sportsbookChipText, on && styles.sportsbookChipTextSelected]}>{name}</Text>
            </TouchableOpacity>
        );
    };

    return (
        <View style={styles.container}>
            <ScreenBackground />
            <Grain />
            <View style={[styles.splashContainer, {
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + (isMobile ? 12 : 32)) : (isMobile ? 12 : 16),
            }]}>
                <NavigationHeader />

                {/* Coming Soon Modal */}
                {showComingSoon && (
                    <Modal visible transparent animationType="fade">
                        <View style={styles.comingSoonOverlay}>
                            <BlurView intensity={80} tint="dark" style={styles.comingSoonBlur}>
                                <View style={styles.comingSoonModal} accessibilityViewIsModal>
                                    <LinearGradient
                                        colors={['rgba(162, 89, 255, 0.1)', 'rgba(162, 89, 255, 0.05)']}
                                        style={styles.comingSoonGradient}
                                    >
                                        <Image
                                            source={require('../../assets/icon.png')}
                                            style={{ width: 64, height: 64, marginBottom: 24 }}
                                            resizeMode="contain"
                                        />
                                        <Text style={styles.comingSoonTitle}>Coming Soon</Text>
                                        <Text style={styles.comingSoonText}>
                                            The Bet Optimizer is currently in beta.{'\n\n'}
                                            Stay tuned for the official launch!
                                        </Text>
                                        <GradientButton
                                            text="Go Home"
                                            onPress={() => navigateTo('Home')}
                                            style={styles.comingSoonButton}
                                            gradientColors={['#A259FF', '#E0BBFF']}
                                        />
                                    </LinearGradient>
                                </View>
                            </BlurView>
                        </View>
                    </Modal>
                )}

                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                <Appear distance={16} style={styles.mainContent}>
                    <Spotlight lift={false} glow="rgba(99,102,241,0.10)" style={{ borderRadius: isMobile ? 12 : 20 }}>
                    <BlurView tint="dark" style={styles.mainContentBlur}>
                        {/* Header Section */}
                        <View style={[styles.titleSection, { position: 'relative', overflow: 'visible' }]}>
                            <AmbientGlow align="left" />
                            <EdgeLine height={compact ? 70 : 110} opacity={0.5} style={{ position: 'absolute', left: -24, right: -24, top: compact ? 10 : 0 }} />
                            <View style={[styles.titleRow, compact && { flexDirection: 'column', alignItems: 'flex-start', gap: 16 }]}>
                                <View style={styles.titleTextContainer}>
                                    <Text style={{ color: '#34D399', fontSize: 12, fontWeight: '700', letterSpacing: 4, marginBottom: 10 }}>OPTIMIZER</Text>
                                    {/* Keyed on the mode so the headline replays when you switch. */}
                                    <KineticHeadline
                                        key={betType}
                                        align="left"
                                        level={1}
                                        lines={sgp ? ['Build the', 'parlay'] : compact ? ['Find the', 'best', 'number'] : ['Find the', 'best number']}
                                        outline={[!sgp && compact ? 2 : 1]}
                                        textStyle={[styles.title, compact ? { fontSize: 32, lineHeight: 36 } : { fontSize: 52, lineHeight: 56 }]}
                                    />
                                    <Text style={styles.subtitle}>
                                        {sgp
                                            ? `Pick a game, stack up to ${MAX_SGP_LEGS} legs, and we price the combination at every book that offers it.`
                                            : 'Narrow by sport, team, player and market. We compare every book and rank by expected value.'}
                                    </Text>
                                </View>
                                <View style={{ alignItems: compact ? 'flex-start' : 'flex-end', gap: 8 }}>
                                    <SegmentedControl
                                        accessibilityLabel="Search type"
                                        options={[{ value: 'Straight', label: 'Straight bets' }, { value: 'SGP', label: 'Same-game parlay' }]}
                                        value={betType}
                                        onChange={switchMode}
                                        accent="rgba(52,211,153,0.18)"
                                        accentBorder="rgba(52,211,153,0.55)"
                                    />
                                    {/* Cache status (an empty pill never shows) */}
                                    {loadingCacheData ? (
                                        <View style={styles.cacheStatus} accessibilityLiveRegion="polite">
                                            <ActivityIndicator size="small" color="#8b5cf6" />
                                            <Text style={styles.cacheStatusText}>Loading today's games…</Text>
                                        </View>
                                    ) : cacheError ? (
                                        <View style={styles.cacheStatus} accessibilityLiveRegion="polite">
                                            <Ionicons name="cloud-offline-outline" size={15} color="#FBBF24" />
                                            <Text style={styles.cacheStatusText}>Live game list unavailable, using the built-in list</Text>
                                        </View>
                                    ) : leagueFixtures.length > 0 ? (
                                        <View style={styles.cacheStatus} accessibilityLiveRegion="polite">
                                            <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: '#34D399' }} />
                                            <Text style={styles.cacheStatusText}>{leagueFixtures.length} {selectedLeague} games loaded</Text>
                                        </View>
                                    ) : null}
                                </View>
                            </View>
                        </View>

                        {/* Main Filters Section */}
                        <View style={[styles.filtersSection, { zIndex: 1000, position: 'relative' }]} {...webData({ optForm: '' })}>
                            <View style={[styles.filtersRow, { zIndex: 500, position: 'relative' }]}>
                                {/* Left Column */}
                                <View style={[styles.filtersColumn, { zIndex: 500 }]}>
                                    <AutocompleteDropdown
                                        label="Sport"
                                        value={selectedSport}
                                        placeholder="Select sport..."
                                        options={Object.keys(sportsData).sort()}
                                        onValueChange={handleSportSelect}
                                        zIndex={300}
                                    />
                                    <AutocompleteDropdown
                                        label="League"
                                        value={selectedLeague}
                                        placeholder={`${leagues.length} available...`}
                                        options={leagues}
                                        onValueChange={handleLeagueSelect}
                                        zIndex={290}
                                    />

                                    {/* Fixture selector for SGP mode */}
                                    {sgp && (
                                        <AutocompleteDropdown
                                            label="Game/Fixture"
                                            value={selectedFixture?.label || ''}
                                            placeholder={loadingFixtures ? 'Loading games...' : selectedLeague ? `${games.length} games available...` : 'Select league first...'}
                                            options={games.map((g) => g.label)}
                                            onValueChange={handleFixtureSelect}
                                            zIndex={280}
                                        />
                                    )}

                                    <AutocompleteDropdown
                                        label="Team"
                                        value={selectedTeam}
                                        placeholder={loadingFixtures ? 'Loading teams...' : `${teams.length} teams available...`}
                                        options={teams}
                                        onValueChange={handleTeamSelect}
                                        zIndex={270}
                                    />

                                    <AutocompleteDropdown
                                        label={loadingPlayers ? 'Player (loading...)' : playerOptions.length > 0 ? `Player (${playerOptions.length} available)` : 'Player (optional)'}
                                        value={selectedPlayer}
                                        placeholder="Type or search player..."
                                        options={playerOptions}
                                        onValueChange={handlePlayerSelect}
                                        zIndex={265}
                                        allowFreeText
                                        emptyText={playerEmptyText(selectedTeam)}
                                        matchOptions={matchPlayerNames}
                                    />
                                </View>

                                {/* Right Column */}
                                <View style={[styles.filtersColumn, { zIndex: 400 }]}>
                                    <AutocompleteDropdown
                                        label="Market Category"
                                        value={selectedMarketCategory}
                                        placeholder="All categories..."
                                        options={categories}
                                        onValueChange={handleCategorySelect}
                                        zIndex={260}
                                    />

                                    <AutocompleteDropdown
                                        label="Market"
                                        value={selectedMarket}
                                        placeholder={`${markets.length} markets available...`}
                                        options={markets}
                                        onValueChange={handleMarketSelect}
                                        zIndex={250}
                                    />

                                    <View style={{ zIndex: 240 }}>
                                        <View style={styles.lineRow}>
                                            <View style={styles.lineValueContainer}>
                                                <Text style={styles.inputLabel}>Line Value</Text>
                                                <View style={[styles.textInputWrap, !!lineIssue && styles.textInputWrapInvalid]} {...webData({ optField: '' })}>
                                                    <TextInput
                                                        placeholder="e.g., 25.5"
                                                        placeholderTextColor="rgba(255,255,255,0.4)"
                                                        style={styles.textInputField}
                                                        value={lineValue}
                                                        onChangeText={(t) => setLineValue(t.replace(/[^0-9.+-]/g, ''))}
                                                        keyboardType="numbers-and-punctuation"
                                                        accessibilityLabel="Line Value"
                                                        aria-invalid={!!lineIssue || undefined}
                                                    />
                                                </View>
                                            </View>
                                            <View style={styles.directionContainer}>
                                                <AutocompleteDropdown
                                                    label="Direction"
                                                    value={lineDirection}
                                                    placeholder="O/U"
                                                    options={['Over', 'Under']}
                                                    onValueChange={setLineDirection}
                                                    zIndex={240}
                                                />
                                            </View>
                                        </View>
                                        {!!lineIssue && <Text style={styles.lineHint} accessibilityLiveRegion="polite">{lineIssue}</Text>}
                                    </View>

                                    {/* Time doesn't apply to a parlay: its game sets the time. */}
                                    {!sgp && (
                                        <AutocompleteDropdown
                                            label="Time Filter"
                                            value={selectedTimeFilter ? TIME_FILTERS.find((t) => t.value === selectedTimeFilter)?.label || '' : ''}
                                            placeholder="All games..."
                                            options={TIME_FILTERS.map((t) => t.label)}
                                            onValueChange={(label) => setSelectedTimeFilter(TIME_FILTERS.find((t) => t.label === label)?.value || null)}
                                            zIndex={230}
                                        />
                                    )}
                                </View>
                            </View>

                            {/* Boost % and Sportsbooks Row */}
                            <View style={[styles.filtersRow, { marginTop: 8, zIndex: 100, position: 'relative' }]}>
                                {/* Boost Percentage Dropdown — Beta (coming soon) */}
                                <View style={{ flex: isMobile ? 1 : 0.4, minWidth: isMobile ? '100%' : 150, zIndex: 90, opacity: 0.4 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <Text style={[styles.inputLabel, { marginBottom: 0 }]}>Profit Boost %</Text>
                                        <View style={{ backgroundColor: 'rgba(162,89,255,0.3)', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2, borderWidth: 1, borderColor: 'rgba(162,89,255,0.6)' }}>
                                            <Text style={{ color: '#c084fc', fontSize: 10, fontWeight: '700', letterSpacing: 0.5 }}>BETA</Text>
                                        </View>
                                    </View>
                                    <AutocompleteDropdown
                                        label=""
                                        value={BOOST_OPTIONS.find((b) => b.value === boostPercent)?.label || 'No Boost'}
                                        placeholder="No Boost"
                                        options={BOOST_OPTIONS.map((b) => b.label)}
                                        onValueChange={() => {}}
                                        zIndex={90}
                                        disabled
                                    />
                                </View>

                                {/* Sportsbooks Multi-Select Dropdown */}
                                <View style={{ flex: 1, position: 'relative', zIndex: showSportsbooksDropdown ? 99999 : 80 }}>
                                    <Text style={styles.inputLabel}>
                                        Sportsbooks ({selectedSportsbooks.length} selected)
                                    </Text>
                                    <TouchableOpacity
                                        style={styles.sportsbooksSelector}
                                        {...webData({ optField: '' })}
                                        onPress={() => setShowSportsbooksDropdown(!showSportsbooksDropdown)}
                                        accessibilityRole="button"
                                        aria-expanded={!!showSportsbooksDropdown}
                                    >
                                        <Text style={styles.sportsbooksSelectorText} numberOfLines={1}>
                                            {selectedSportsbooks.length === 0
                                                ? 'All Sportsbooks (click to filter)'
                                                : selectedSportsbooks.slice(0, 3).join(', ') + (selectedSportsbooks.length > 3 ? ` +${selectedSportsbooks.length - 3} more` : '')}
                                        </Text>
                                        <Text style={styles.sportsbooksArrow}>{showSportsbooksDropdown ? '▲' : '▼'}</Text>
                                    </TouchableOpacity>

                                    {showSportsbooksDropdown && (
                                        <View style={styles.sportsbooksDropdown}>
                                            <View style={styles.sportsbooksDropdownHeader}>
                                                {myBookNames.length > 0 && (
                                                    <TouchableOpacity
                                                        onPress={() => setSelectedSportsbooks(myBookNames)}
                                                        style={[styles.sportsbooksQuickAction, { backgroundColor: 'rgba(162,89,255,0.2)' }]}
                                                        accessibilityRole="button"
                                                    >
                                                        <Text style={[styles.sportsbooksQuickActionText, { color: '#c084fc' }]}>⭐ My Books</Text>
                                                    </TouchableOpacity>
                                                )}
                                                <TouchableOpacity
                                                    onPress={() => setSelectedSportsbooks(POPULAR_SPORTSBOOKS)}
                                                    style={styles.sportsbooksQuickAction}
                                                    accessibilityRole="button"
                                                >
                                                    <Text style={styles.sportsbooksQuickActionText}>Select Popular</Text>
                                                </TouchableOpacity>
                                                <TouchableOpacity
                                                    onPress={() => setSelectedSportsbooks([])}
                                                    style={[styles.sportsbooksQuickAction, { backgroundColor: 'rgba(248,113,113,0.2)' }]}
                                                    accessibilityRole="button"
                                                >
                                                    <Text style={[styles.sportsbooksQuickActionText, { color: '#f87171' }]}>Clear</Text>
                                                </TouchableOpacity>
                                                <TouchableOpacity onPress={() => setShowSportsbooksDropdown(false)} style={styles.sportsbooksCloseBtn} accessibilityRole="button">
                                                    <Text style={styles.sportsbooksCloseBtnText}>Done</Text>
                                                </TouchableOpacity>
                                            </View>

                                            <ScrollView style={styles.sportsbooksList} nestedScrollEnabled>
                                                {/* My Books — pinned to top if user has preferences set */}
                                                {myBookNames.length > 0 && (
                                                    <>
                                                        <Text style={[styles.sportsbooksTierLabel, { color: '#c084fc' }]}>⭐ My Books</Text>
                                                        <View style={styles.sportsbooksGridDropdown} {...webData({ optChips: '' })}>
                                                            {myBookNames.map((n) => chip(n, 'mybook_', true))}
                                                        </View>
                                                    </>
                                                )}
                                                {TIERS.map(([tier, title, icon]) => (
                                                    <React.Fragment key={tier}>
                                                        <View style={styles.sportsbooksTierRow}><Ionicons name={icon} size={12} color="#34D399" /><Text style={styles.sportsbooksTierLabel}>{title}</Text></View>
                                                        <View style={styles.sportsbooksGridDropdown} {...webData({ optChips: '' })}>
                                                            {ALL_SPORTSBOOKS.filter((b) => b.tier === tier).map((b) => chip(b.name))}
                                                        </View>
                                                    </React.Fragment>
                                                ))}
                                            </ScrollView>
                                        </View>
                                    )}
                                </View>
                            </View>

                            {/* Selected Sportsbooks Summary */}
                            {selectedSportsbooks.length > 0 && (
                                <View style={styles.selectedSportsbooksSummary}>
                                    <View style={styles.selectedSportsbooksHeader}>
                                        <Text style={styles.selectedSportsbooksLabel}>
                                            Filtering: {selectedSportsbooks.length} book{selectedSportsbooks.length !== 1 ? 's' : ''}
                                        </Text>
                                        <TouchableOpacity onPress={() => setSelectedSportsbooks([])} accessibilityRole="button" accessibilityLabel="Clear sportsbook filter">
                                            <Text style={styles.clearSportsbooksBtn}>Clear</Text>
                                        </TouchableOpacity>
                                    </View>
                                    <View style={styles.selectedSportsbooksChips}>
                                        {selectedSportsbooks.map((book) => (
                                            <TouchableOpacity
                                                key={book}
                                                style={styles.selectedSportsbookChip}
                                                onPress={() => toggleSportsbook(book)}
                                                accessibilityRole="button"
                                                accessibilityLabel={`Remove ${book}`}
                                            >
                                                <Text style={styles.selectedSportsbookChipText}>{book}</Text>
                                                <Text style={styles.selectedSportsbookRemove}>×</Text>
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                </View>
                            )}

                            {/* Added Queries Display (Straight mode) */}
                            {!sgp && queries.length > 0 && (
                                <View style={styles.queriesSection}>
                                    <Text style={styles.sectionLabel}>Combined Queries ({queries.length})</Text>
                                    {queries.map((q, index) => (
                                        <View key={q.id} style={styles.queryCard}>
                                            <View style={styles.queryCardContent}>
                                                <Text style={styles.queryCardNumber}>Query {index + 1}</Text>
                                                <Text style={styles.queryCardText}>{q.text}</Text>
                                            </View>
                                            <TouchableOpacity
                                                style={styles.removeButton}
                                                onPress={() => removeQuery(q.id)}
                                                accessibilityRole="button"
                                                accessibilityLabel={`Remove query ${index + 1}`}
                                            >
                                                <Text style={styles.removeButtonText}>×</Text>
                                            </TouchableOpacity>
                                        </View>
                                    ))}
                                </View>
                            )}

                            {/* SGP Legs Display */}
                            {sgp && legs.length > 0 && (
                                <View style={styles.legsSection}>
                                    <Text style={styles.sectionLabel}>Parlay Legs ({legs.length} of {MAX_SGP_LEGS})</Text>
                                    {legs.map((leg, index) => (
                                        <View key={leg.id} style={styles.legCard}>
                                            <View style={styles.legCardContent}>
                                                <Text style={styles.legCardNumber}>Leg {index + 1}</Text>
                                                <Text style={styles.legCardDescription}>{leg.description}</Text>
                                                {!!leg.fixture?.label && <Text style={styles.legCardDetails}>{leg.fixture.label}</Text>}
                                            </View>
                                            <TouchableOpacity
                                                style={styles.removeButton}
                                                onPress={() => removeLeg(leg.id)}
                                                accessibilityRole="button"
                                                accessibilityLabel={`Remove leg ${index + 1}`}
                                            >
                                                <Text style={styles.removeButtonText}>×</Text>
                                            </TouchableOpacity>
                                        </View>
                                    ))}
                                </View>
                            )}
                            {!!notice && <Text style={styles.notice} accessibilityLiveRegion="polite">{notice}</Text>}

                            {/* Query Preview */}
                            <View style={styles.queryPreview} accessibilityLiveRegion="polite">
                                <Text style={styles.queryPreviewLabel}>YOUR SEARCH</Text>
                                <Text style={[styles.queryPreviewText, !finalQuery && { opacity: 0.5 }]} selectable>
                                    {finalQuery || (sgp ? 'Pick a game and add at least one leg' : 'Pick a sport, team, player or market to start')}
                                </Text>
                            </View>

                            {/* Error Message */}
                            {!!errorMsg && (
                                <View style={styles.errorContainer} accessibilityLiveRegion="assertive">
                                    <Text style={styles.errorText}>{errorMsg}</Text>
                                </View>
                            )}

                            {/* Action Buttons */}
                            <View style={styles.actionButtons}>
                                <PressableScale
                                    style={[styles.actionButton, styles.addButton]}
                                    hoverStyle={styles.addButtonHover}
                                    disabledStyle={styles.actionButtonDisabled}
                                    onPress={sgp ? addLeg : addQuery}
                                    disabled={sgp && legs.length >= MAX_SGP_LEGS}
                                    accessibilityRole="button"
                                    accessibilityLabel={sgp ? 'Add Leg' : 'Add this as another query'}
                                    {...webData({ optBtn: 'add', pressable: 'scale' })}
                                >
                                    <View {...webData({ optIcon: 'plus' })}>
                                        <Ionicons name="add-circle-outline" size={18} color="#fff" />
                                    </View>
                                    <Text style={styles.actionButtonText}>{sgp ? 'Add Leg' : 'Add Query'}</Text>
                                </PressableScale>

                                <PressableScale
                                    style={[styles.actionButton, styles.clearButton]}
                                    hoverStyle={styles.clearButtonHover}
                                    onPress={clearAllFilters}
                                    accessibilityRole="button"
                                    {...webData({ optBtn: 'clear', pressable: 'scale' })}
                                >
                                    <View {...webData({ optIcon: 'spin' })}>
                                        <Ionicons name="refresh-outline" size={18} color="#fff" />
                                    </View>
                                    <Text style={styles.actionButtonText}>Clear All</Text>
                                </PressableScale>

                                {canAddBackgroundQuery && !backgroundQueryMode && (
                                    <PressableScale
                                        style={[styles.actionButton, styles.clearButton]}
                                        hoverStyle={styles.clearButtonHover}
                                        disabledStyle={styles.actionButtonDisabled}
                                        onPress={() => runSearch(true)}
                                        disabled={!finalQuery || loading}
                                        accessibilityRole="button"
                                        accessibilityLabel="Run this search in the background"
                                        {...webData({ optBtn: 'background', pressable: 'scale' })}
                                    >
                                        <View {...webData({ optIcon: 'stack' })}>
                                            <Ionicons name="layers-outline" size={18} color="#fff" />
                                        </View>
                                        <Text style={styles.actionButtonText}>In background</Text>
                                    </PressableScale>
                                )}
                                <MagneticButton
                                    label={loading ? 'Searching…' : sgp ? 'Optimize parlay' : 'Search'}
                                    icon={sgp ? 'flash' : 'search'}
                                    arrow={false}
                                    busy={loading}
                                    busyLabel="Searching…"
                                    disabled={!finalQuery}
                                    onPress={() => runSearch(false)}
                                    style={[styles.actionButton, styles.searchButton, { minHeight: 0 }]}
                                    textStyle={styles.searchButtonText}
                                    accessibilityLabel={sgp ? 'Optimize this parlay' : 'Search'}
                                />
                            </View>
                        </View>
                    </BlurView>
                    </Spotlight>
                </Appear>
                </ScrollView>
            </View>
        </View>
    );
};

export default SameGameParlayScreen;
